// "3 unsaved changes" in a form's Save / Cancel bar (TGCLOUD-1902, rule 5): how
// many fields hold a change, and the way to them. Each click focuses the next
// changed field in screen order, round again from the first. With no `onJump`
// it looks for them in the dialog it sits in, so a modal's bar needs nothing
// but the count.

import React from 'react';
import { Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import { changedPalette } from 'src/theme/palette';
import { focusChangedElement, nextChangedElement } from './focusChangedField';

const ROOT = '[data-changed-root], .MuiDialog-paper, [role="dialog"]';

/**
 * @param {object} props
 * @param {number} props.count  the changed fields; nothing is drawn for none
 * @param {Function} [props.onJump]  go to the next change yourself (a form that
 *   has to open a collapsed section first)
 */
export default function UnsavedChangeCount({ count, onJump, sx }) {
  const t = useAutoTranslation();
  const palette = changedPalette(useTheme());
  const lastRef = React.useRef(null);
  if (!Number.isFinite(count) || count <= 0) return null;

  const jump = (event) => {
    if (onJump) {
      onJump(event);
      return;
    }
    const root = event.currentTarget.closest(ROOT) || document.body;
    const next = nextChangedElement(root, lastRef.current);
    lastRef.current = next;
    focusChangedElement(next);
  };

  return (
    <Button
      variant="text"
      size="small"
      onClick={jump}
      title={t('Go to the next unsaved change')}
      data-testid="unsaved-change-count"
      sx={[
        {
          color: palette.text,
          fontWeight: 600,
          whiteSpace: 'nowrap',
          textTransform: 'none'
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : [])
      ]}
    >
      {count === 1
        ? t('1 unsaved change')
        : t('{{count}} unsaved changes', { count })}
    </Button>
  );
}
